// Arabic Services index — mirrors the English Services index with the
// same four grouped blocks (Build · Intelligence · Secure · Run · plus an
// alphabetical directory). Arabic slugs that are not yet translated fall
// through to the English page via the /ar/* Route wildcard.
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Cpu, Wrench, Rocket } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ArPageHero, ArBlock, ArContactCTA } from "@/pages/ar/_ArShared";
import { SERVICES_AR } from "@/data/services-ar";

const SERVICE_GROUPS = [
  {
    key: "build",
    icon: Wrench,
    title: "البناء",
    lede: "البرمجيات نفسها — تُصمَّم، تُكتب، تُختبر، تُشحن.",
    links: [
      { slug: "custom-software-development-company-in-oman" },
      { slug: "saas-development-company-in-oman" },
      { slug: "mobile-app-development-company-in-oman" },
      { slug: "erp-software-development-company-in-oman" },
      { slug: "crm-software-development-company-in-oman" },
      { slug: "ui-ux-design-branding-oman" },
    ],
  },
  {
    key: "intelligence",
    icon: Cpu,
    title: "الذكاء",
    lede: "تحويل البرمجيات إلى شيء يفكّر، وتحويل الزيارات إلى عملاء محتملين.",
    links: [
      { slug: "digital-marketing-company-in-oman" },
      { label: "مختبر الذكاء الاصطناعي", desc: "ذكاء اصطناعي يصمد في الإنتاج.", to: "/ar/ai-lab" },
    ],
  },
  {
    key: "secure",
    icon: ShieldCheck,
    title: "الحوكمة",
    lede: "الحوكمة، المخاطر، والامتثال — الأدلة، لا النوايا.",
    links: [
      { label: "Meyar Enterprise GRC", desc: "الحوكمة والمخاطر مترابطتان.", to: "/ar/meyar/enterprise-grc" },
      { label: "Meyar Cyber Security GRC", desc: "ضوابط مربوطة بإطار يقبله المدقق.", to: "/ar/meyar/cyber-security-grc" },
    ],
  },
  {
    key: "run",
    icon: Rocket,
    title: "التشغيل",
    lede: "أين يعيش، كيف يبقى يعمل، ومن يجيب حين لا يعمل.",
    links: [
      { slug: "cloud-devops-oman" },
      { slug: "application-support-maintenance-oman" },
    ],
  },
];

function GroupLink({ label, desc, to }) {
  return (
    <Link
      to={to}
      className="group flex items-start justify-between gap-3 rounded-lg border border-wafa-deep/10 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.2)]"
    >
      <div>
        <div className="font-heading text-sm font-semibold text-wafa-deep group-hover:text-wafa-primary">{label}</div>
        {desc ? <p className="mt-1 text-xs text-wafa-ink/70">{desc}</p> : null}
      </div>
      <ArrowLeft className="mt-1 h-4 w-4 shrink-0 text-wafa-primary transition-transform group-hover:-translate-x-1" />
    </Link>
  );
}

export default function ServicesIndexAr() {
  const list = Object.values(SERVICES_AR)
    .map((s) => ({ ...s, path: `/ar/services/${s.slug}` }))
    .sort((a, b) => (a.navLabel || a.h1).localeCompare(b.navLabel || b.h1, "ar"));

  return (
    <div data-testid="services-index-ar">
      <Seo
        title="الخدمات"
        description="أربعة مجالات، فريق تسليم واحد في عُمان: البناء، الذكاء، الحوكمة، والتشغيل."
      />
      <ArPageHero
        eyebrow="الخدمات"
        h1="ماذا نقدّم"
        sub="البناء، الذكاء الاصطناعي، الحوكمة، والتشغيل. معظم الشركات تشتري هذه الأربعة من أربعة موردين مختلفين، وتقضي وقتها في تحديد المسؤول عند حدوث مشكلة. نحن نقدّم الأربعة، ويعود جميعها لشخص واحد."
        breadcrumbs={[{ label: "الرئيسية", to: "/ar" }, { label: "الخدمات" }]}
      />

      <ArBlock tint="white">
        <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-wafa-primary">
          أربعة مجالات، فريق تسليم واحد في عُمان.
        </div>
        <div className="space-y-10">
          {SERVICE_GROUPS.map((g) => {
            const Icon = g.icon;
            const links = g.links.map((l) => {
              if (l.slug) {
                const svc = SERVICES_AR[l.slug];
                return svc
                  ? { label: svc.navLabel || svc.h1, desc: null, to: `/ar/services/${svc.slug}` }
                  : null;
              }
              return { label: l.label, desc: l.desc, to: l.to };
            }).filter(Boolean);
            return (
              <div key={g.key} data-testid={`service-group-ar-${g.key}`} className="rounded-2xl border border-wafa-deep/10 bg-wafa-cream/30 p-6 lg:p-8">
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-wafa-primary/10 text-wafa-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-wafa-deep sm:text-3xl">{g.title}</h3>
                    <p className="mt-1 text-sm text-wafa-ink/75 lg:text-base">{g.lede}</p>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  {links.map((l, i) => (
                    <GroupLink key={i} label={l.label} desc={l.desc} to={l.to} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <h3 className="font-heading text-lg font-semibold text-wafa-deep">جميع الخدمات، بترتيب أبجدي</h3>
          <p className="mt-1 text-sm text-wafa-ink/60">للزوّار الذين يعرفون ما يبحثون عنه.</p>
          <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {list.map((s) => (
              <Link
                key={s.slug}
                to={s.path}
                data-testid={`index-card-ar-${s.slug}`}
                className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:border-wafa-deep/25 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
              >
                <div className="font-heading text-lg font-semibold text-wafa-deep group-hover:text-wafa-primary">
                  {s.navLabel || s.h1}
                </div>
                {s.intro ? (
                  <p className="mt-3 line-clamp-3 text-sm text-wafa-ink/70">
                    {s.intro.length > 160 ? s.intro.slice(0, 160) + "…" : s.intro}
                  </p>
                ) : null}
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-wafa-deep">
                  اقرأ المزيد <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </ArBlock>

      <ArContactCTA heading="غير متأكدين أيها تحتاجون؟" />
    </div>
  );
}
